import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, X, FileXls, DownloadSimple } from '@phosphor-icons/react';
import type { ExpectedField } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { SheetRulesPopover } from './SheetRulesPopover';
import { WizardFooter } from './ImportWizardFooterSlot';
import { estimatePreviewColumnWidths, compactFieldKey, compactKey } from './importWizardHelpers';
import { TruncatedText } from '../../../components/common/TruncatedText';

interface ImportUploadStepProps {
  payoutModel?: string;
  /** This campaign's importable columns — chips above the drop zone and the
   *  header row of the downloadable template. Empty while checkStatus (fired
   *  when the wizard opens) is still in flight. */
  expectedFields: ExpectedField[];
  busy: boolean;
  onUpload: (file: File) => Promise<void>;
}

/** Builds a blank .xlsx with just the header row (one column per expected
 *  field, machine-readable key — not the display label) and triggers a
 *  browser download. Loads the xlsx lib lazily, same as the preview below. */
async function downloadTemplate(expectedFields: ExpectedField[]) {
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.aoa_to_sheet([expectedFields.map(compactFieldKey)]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'creator-sheet-template.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

interface SheetPreview {
  rows: string[][];
  /** Data rows beyond the ones shown (excludes the header row). */
  moreRows: number;
}

/** First 6 rows (header + up to 5 data rows) of a workbook's first sheet, as
 *  strings, for a quick client-side glance before the real server-side parse.
 *  Loads the xlsx lib lazily — most sessions never open this wizard. */
async function readSheetPreview(file: File): Promise<SheetPreview> {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = firstSheetName ? workbook.Sheets[firstSheetName] : undefined;
  if (!sheet) return { rows: [], moreRows: 0 };
  const allRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false, defval: '' });
  const rows = allRows.slice(0, 6).map((row) => row.map((cell) => String(cell)));
  const dataRowCount = Math.max(0, allRows.length - 1);
  return { rows, moreRows: Math.max(0, dataRowCount - (rows.length - 1)) };
}

export function ImportUploadStep({ payoutModel, expectedFields, busy, onUpload }: ImportUploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<SheetPreview | 'loading' | 'error' | null>(null);

  const selectFile = (next: File | null) => {
    setFile(next);
    if (!next) {
      setPreview(null);
      return;
    }
    setPreview('loading');
    readSheetPreview(next)
      .then(setPreview)
      .catch(() => setPreview('error'));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-body-sm-regular text-muted-foreground">
          Use these exact Excel columns for auto-populate.
        </p>
        <SheetRulesPopover payoutModel={payoutModel} expectedFields={expectedFields} />
      </div>

      {expectedFields.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {expectedFields.map((f) => (
            <span
              key={f.key}
              className="inline-flex h-[22px] items-center rounded-full border border-border px-3 text-caption-medium text-muted-foreground"
            >
              {compactFieldKey(f)}
              {f.required && <span className="ml-0.5 text-rose-600">*</span>}
            </span>
          ))}
        </div>
      )}

      {/* Not role="button"/tabIndex — it contains real interactive elements
          (the "Choose file"/"Remove file" buttons, the file input) and an
          interactive ARIA role wrapping other interactive elements is an
          axe nested-interactive violation. Keyboard users don't lose
          anything: the "Choose file" button below is a real, independently
          focusable <button> that does the same thing. The onClick here is
          just a mouse convenience so clicking anywhere in the zone (not
          only the button) opens the file picker. */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          selectFile(e.dataTransfer.files?.[0] ?? null);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-[1.5px] border-dashed text-center transition-colors',
          file ? 'px-4 py-3' : 'px-6 py-8',
          dragActive ? 'border-foreground bg-muted' : 'border-border bg-muted/50 hover:bg-muted',
        )}
      >
        {file ? (
          <div className="flex w-full items-center gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-card">
              <FileXls size={16} className="text-foreground" />
            </div>
            <TruncatedText
              text={file.name}
              className="min-w-0 flex-1 text-left text-body-sm-medium text-foreground"
            />
            <button
              type="button"
              aria-label="Remove file"
              onClick={(e) => {
                e.stopPropagation();
                selectFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-card">
              <UploadSimple size={20} className="text-foreground" />
            </div>
            <p className="text-body-sm-medium text-foreground">Drag and drop your file here</p>
            <p className="text-caption-regular text-muted-foreground">.xlsx or .xls only</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            >
              Choose file
            </Button>
          </>
        )}
        <label className="sr-only" htmlFor="import-wizard-file-input">Choose file</label>
        <input
          ref={fileInputRef}
          id="import-wizard-file-input"
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
          className="hidden"
        />
      </div>

      {preview === 'loading' && (
        <p className="text-caption-regular text-muted-foreground">Reading preview…</p>
      )}
      {preview === 'error' && (
        <p className="text-caption-regular text-muted-foreground">
          Couldn&apos;t preview this file — you can still upload it.
        </p>
      )}
      {preview && preview !== 'loading' && preview !== 'error' && preview.rows.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full border-collapse text-left text-[12px]" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                {/* Sized off the DISPLAYED (compacted) header text, not the
                    sheet's raw one — otherwise a long raw header like
                    "engagement_rate" would still claim width for text that
                    never actually renders. */}
                {estimatePreviewColumnWidths([preview.rows[0]?.map(compactKey) ?? [], ...preview.rows.slice(1)]).map((width, i) => (
                  <col key={i} style={{ width }} />
                ))}
              </colgroup>
              <thead>
                <tr className="bg-muted">
                  {preview.rows[0]?.map((header, i) => (
                    <th
                      key={i}
                      className="border-b border-border px-3 py-2 font-semibold text-muted-foreground"
                    >
                      <TruncatedText
                        text={header ? compactKey(header) : ''}
                        content={header}
                        placeholder="—"
                        className="block"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(1).map((row, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    {row.map((cell, j) => (
                      <td key={j} className="px-3 py-2 text-foreground">
                        <TruncatedText text={cell || ''} content={cell} placeholder="—" className="block" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.moreRows > 0 && (
            <p className="text-caption-regular text-muted-foreground">+{preview.moreRows} more row{preview.moreRows === 1 ? '' : 's'}</p>
          )}
        </div>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <button
            type="button"
            disabled={expectedFields.length === 0}
            onClick={() => { downloadTemplate(expectedFields).catch(() => toast.error("Couldn't generate the template — try again.")); }}
            className="inline-flex items-center gap-1.5 text-body-sm-medium text-foreground underline underline-offset-2 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            <DownloadSimple size={14} /> Download template
          </button>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={!file}
            onClick={() => { if (file) onUpload(file).catch(() => {}); }}
          >
            {!busy && <UploadSimple size={14} />}
            {busy ? 'Uploading…' : 'Upload sheet'}
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
